/**
 * Plan-compatibility helpers for billing groups.
 *
 * A billing group takes its rate plans from its founding members, so creating
 * a group requires at least one investor and every investor on the create call
 * must already share the same set of rate plans. These helpers derive an
 * investor's plan keys from the groups they currently belong to so the UI can
 * restrict the founding investor selection to plan-compatible investors.
 */

/**
 * Build a stable plan key for a subscription so plans can be compared across
 * groups regardless of where the subscription object came from.
 * @param {object} subscription
 * @returns {string}
 */
export function getSubscriptionPlanKey(subscription = {}) {
    const plan = subscription?.rate_plan || {};
    return `${subscription.rate_plan_type || plan.type || 'PLAN'}:${plan.id || plan.name || 'unknown'}`;
}

/**
 * Return the sorted plan keys of a billing group.
 * @param {object} group
 * @returns {string[]}
 */
export function getGroupPlanKeys(group = {}) {
    const subscriptions = Array.isArray(group?.active_subscriptions) ? group.active_subscriptions : [];

    return subscriptions.map(getSubscriptionPlanKey).sort();
}

/**
 * Return the sorted, de-duplicated plan keys an investor currently has across
 * all the (non-deleted) billing groups they belong to.
 * @param {string} investorId
 * @param {object[]} groups
 * @returns {string[]}
 */
export function getInvestorPlanKeys(investorId, groups = []) {
    const keys = new Set();

    for (const group of groups) {
        if (group?.deleted_at) {
            continue;
        }

        const investorIds = Array.isArray(group.investor_ids) ? group.investor_ids : [];

        if (!investorIds.includes(investorId)) {
            continue;
        }

        getGroupPlanKeys(group).forEach(key => keys.add(key));
    }

    return [...keys].sort();
}

/**
 * Collect the de-duplicated subscriptions an investor currently has across all
 * the (non-deleted) billing groups they belong to. Used when a new group is
 * founded on an investor so it can carry their plans.
 * @param {string} investorId
 * @param {object[]} groups
 * @returns {object[]}
 */
export function getInvestorSubscriptions(investorId, groups = []) {
    const seen = new Set();
    const subscriptions = [];

    for (const group of groups) {
        if (group?.deleted_at) {
            continue;
        }

        const investorIds = Array.isArray(group.investor_ids) ? group.investor_ids : [];

        if (!investorIds.includes(investorId)) {
            continue;
        }

        const groupSubscriptions = Array.isArray(group.active_subscriptions) ? group.active_subscriptions : [];

        for (const subscription of groupSubscriptions) {
            const key = getSubscriptionPlanKey(subscription);

            if (!seen.has(key)) {
                seen.add(key);
                subscriptions.push(subscription);
            }
        }
    }

    return subscriptions;
}

/**
 * Whether two plan key sets represent the same rate plans.
 * @param {string[]} firstKeys
 * @param {string[]} secondKeys
 * @returns {boolean}
 */
export function samePlanKeys(firstKeys = [], secondKeys = []) {
    const a = [...firstKeys].sort().join('|');
    const b = [...secondKeys].sort().join('|');

    return a === b;
}
